import { 
  Component, 
  ChangeDetectionStrategy, 
  DestroyRef,
  inject, 
  signal, 
  computed,
  OnInit
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DIALOG_PRESETS } from '../../../../../shared/utils/dialog-presets';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';

import { PessoaStore } from '../../data-access/pessoa.store';
import { PessoaFoto } from '../pessoa-foto/pessoa-foto';
import { PessoaDetalhe, ResultadoConsulta } from '../../models';
import { NgxMaskPipe } from 'ngx-mask';
import { 
  IdentificarPessoaModal, 
  IdentificarPessoaModalResult 
} from '../identificar-pessoa-modal/identificar-pessoa-modal';
import {
  CadastroPessoaWizard,
  CadastroPessoaWizardData,
  CadastroPessoaWizardResult
} from '../cadastro-pessoa-wizard/cadastro-pessoa-wizard';

/**
 * Dados de entrada do modal (opcionais)
 */
export interface BuscaPessoaModalData {
  /** Se true, mostra botão de cadastro manual quando não encontrar resultados */
  permitirCadastroManual?: boolean;
}

/**
 * Resultado retornado pelo modal de busca
 */
export interface BuscaPessoaModalResult {
  pessoa?: PessoaDetalhe;
  cancelado: boolean;
}

/**
 * BuscaPessoaModal
 * 
 * Modal para buscar e selecionar uma pessoa por nome.
 * Utiliza o PessoaStore para fazer a consulta.
 */
@Component({
  selector: 'app-busca-pessoa-modal',
  templateUrl: './busca-pessoa-modal.html',
  styleUrl: './busca-pessoa-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    PessoaFoto,
    NgxMaskPipe
  ]
})
export class BuscaPessoaModal implements OnInit {
  
  private dialogRef = inject(MatDialogRef<BuscaPessoaModal>);
  private pessoaStore = inject(PessoaStore);
  private dialog = inject(MatDialog);
  private data = inject<BuscaPessoaModalData>(MAT_DIALOG_DATA, { optional: true });
  
  // Form control para busca
  searchControl = new FormControl<string>('');
  
  // Estado local
  resultados = signal<PessoaDetalhe[]>([]);
  isLoading = signal(false);
  erro = signal<string | null>(null);
  termoBuscado = signal<string>('');
  
  // Configurações recebidas via data
  permitirCadastroManual = signal(false);
  
  // Computed
  temResultados = computed(() => this.resultados().length > 0);
  semResultados = computed(() => 
    !this.isLoading() && 
    this.termoBuscado().length >= 3 && 
    this.resultados().length === 0 &&
    !this.erro()
  );
  
  ngOnInit(): void {
    if (this.data) {
      if (this.data.permitirCadastroManual !== undefined) {
        this.permitirCadastroManual.set(this.data.permitirCadastroManual);
      }
    }
  }
  
  constructor() {
    // Configura debounce na busca (injection context para takeUntilDestroyed)
    inject(DestroyRef);
    this.searchControl.valueChanges.pipe(
      takeUntilDestroyed(),
      debounceTime(2000),
      distinctUntilChanged(),
      filter(value => !!value && value.length >= 3)
    ).subscribe(value => {
      this.buscarPessoa(value!);
    });
  }
  
  /**
   * Busca pessoa por nome
   */
  async buscarPessoa(nome: string): Promise<void> {
    this.isLoading.set(true);
    this.erro.set(null);
    this.termoBuscado.set(nome);
    
    try {
      const resultado = await this.pessoaStore.consultarPessoa({ nome });
      
      if (resultado === ResultadoConsulta.MULTIPLOS) {
        // Múltiplos resultados
        this.resultados.set(this.pessoaStore.pessoasEncontradas());
      } else if (resultado === ResultadoConsulta.SUCESSO) {
        // Um resultado
        const pessoa = this.pessoaStore.pessoaAtual();
        this.resultados.set(pessoa ? [pessoa] : []);
      } else if (resultado === ResultadoConsulta.NENHUM_RESULTADO) {
        this.resultados.set([]);
      } else {
        // Erro
        this.erro.set(this.pessoaStore.erro() || 'Erro ao buscar pessoa');
        this.resultados.set([]);
      }
    } catch (e: any) {
      this.erro.set(e.message || 'Erro ao buscar pessoa');
      this.resultados.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }
  
  /**
   * Seleciona uma pessoa e fecha o modal.
   * Se a pessoa tem CPF, faz lookup de detalhe para obter idPessoaNexus.
   * Se não for validada, abre o modal de identificação primeiro.
   */
  async selecionarPessoa(pessoa: PessoaDetalhe): Promise<void> {
    if (!pessoa.dadosValidados) {
      await this.abrirModalIdentificacao(pessoa);
      return;
    }

    // Pessoa da lista pode não ter idPessoaNexus (endpoint de lista não retorna).
    // Faz lookup de detalhe pelo CPF para obter o dado completo.
    if (!pessoa.idPessoaNexus && pessoa.cpf) {
      this.isLoading.set(true);
      try {
        const resultado = await this.pessoaStore.consultarPessoa({ cpf: pessoa.cpf });
        const pessoaCompleta = this.pessoaStore.pessoaAtual();
        if (resultado === ResultadoConsulta.SUCESSO && pessoaCompleta?.idPessoaNexus) {
          this.dialogRef.close({ pessoa: pessoaCompleta, cancelado: false } as BuscaPessoaModalResult);
          return;
        }
      } catch {
        // Fallback: usa a pessoa original da lista
      } finally {
        this.isLoading.set(false);
      }
    }

    this.dialogRef.close({ pessoa, cancelado: false } as BuscaPessoaModalResult);
  }

  /**
   * Abre o modal de identificação para pessoa não validada
   */
  private async abrirModalIdentificacao(pessoaOriginal: PessoaDetalhe): Promise<void> {
    const identificarDialogRef = this.dialog.open(IdentificarPessoaModal, {
      ...DIALOG_PRESETS.form,
      data: { pessoaOriginal },
      disableClose: false,
    });

    const result = await firstValueFrom(identificarDialogRef.afterClosed()) as IdentificarPessoaModalResult | undefined;

    if (!result) {
      return; // Modal fechado sem ação
    }

    if (result.acao === 'substituir' && result.pessoaValidada) {
      // Substitui na lista e retorna a pessoa validada
      this.pessoaStore.substituirPessoaNaLista(pessoaOriginal.idInterno, result.pessoaValidada);
      
      // Atualiza a lista local também
      const listaAtual = this.resultados();
      const novaLista = listaAtual.map(p => 
        p.idInterno === pessoaOriginal.idInterno ? result.pessoaValidada! : p
      );
      this.resultados.set(novaLista);
      
      // Fecha o modal de busca com a pessoa validada
      const buscarResult: BuscaPessoaModalResult = {
        pessoa: result.pessoaValidada,
        cancelado: false
      };
      this.dialogRef.close(buscarResult);
    } else if (result.acao === 'usar_original') {
      // Usa a pessoa original (não validada)
      const buscarResult: BuscaPessoaModalResult = {
        pessoa: pessoaOriginal,
        cancelado: false
      };
      this.dialogRef.close(buscarResult);
    }
    // Se cancelou, não faz nada (volta para a lista)
  }
  
  /**
   * Cancela e fecha o modal
   */
  cancelar(): void {
    const result: BuscaPessoaModalResult = {
      cancelado: true
    };
    this.dialogRef.close(result);
  }
  
  /**
   * Limpa a busca
   */
  limpar(): void {
    this.searchControl.setValue('');
    this.resultados.set([]);
    this.termoBuscado.set('');
    this.erro.set(null);
  }

  /**
   * Abre o wizard de cadastro de pessoa
   */
  async abrirCadastroManual(): Promise<void> {
    const wizardRef = this.dialog.open(CadastroPessoaWizard, {
      ...DIALOG_PRESETS.formCompact,
      data: {
        nome: this.termoBuscado()
      } as CadastroPessoaWizardData,
      disableClose: true,
    });

    const result = await firstValueFrom(wizardRef.afterClosed()) as CadastroPessoaWizardResult | undefined;

    if (result?.sucesso && result.pessoa) {
      const buscarResult: BuscaPessoaModalResult = {
        pessoa: result.pessoa,
        cancelado: false
      };
      this.dialogRef.close(buscarResult);
    }
  }
}
